import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { StandardEvents } from '@shopify/events';

/**
 * Returns a card's gallery to the featured image. Called before the dense views take the
 * carousel away: a shopper who had swiped that card to another image would otherwise be left
 * looking at it with no arrows and no swipe left to get back, so the grid would show a row of
 * products by whichever photo each card happened to be stopped on.
 * @param {Element} slider
 */
function resetToFeaturedSlide(slider) {
  if (!('select' in slider) || typeof slider.select !== 'function') return;

  slider.select(0, undefined, { animate: false });
}

/**
 * Stopgap for the wishlist app, reported to its team. The app injects its heart by scanning the
 * page for product cards, and re-scans from a MutationObserver that only reacts to cards being
 * ADDED. A grid update here patches the cards in place instead, so the heart, which the server
 * HTML knows nothing about, is dropped by the morph and never put back. Remove once the app
 * notices a card it has already seen.
 */
function reapplyWishlistIcons() {
  if (typeof window.bnApplyIconOnProducts === 'function') window.bnApplyIconOnProducts();
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Apply the initial state, then keep it in sync with view changes (attribute) and
    // cards appended to the grid (childList — infinite scroll, load more).
    this.#updateCardSliders();
    const { grid } = this.refs;
    if (grid) {
      this.#cardSliderObserver.observe(grid, {
        childList: true,
        attributes: true,
        attributeFilter: ['product-grid-view'],
      });
    }

    // Filtering reaches neither of those signals: the section is morphed in place, so
    // the grid's own children are reused rather than added, and `product-grid-view` is
    // in the morph's preserved attributes so it never changes. The cards underneath are
    // new all the same, and arrive from the server with their carousels enabled.
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleResultsUpdate);
    document.addEventListener(StandardEvents.searchUpdate, this.#handleResultsUpdate);
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#cardSliderObserver.disconnect();
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleResultsUpdate);
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleResultsUpdate);
  }

  /**
   * Re-applies the per-card slider state once the grid has finished rendering, and asks the
   * wishlist app for its hearts back.
   * @param {{ promise?: Promise<unknown> }} [event]
   */
  #handleResultsUpdate = (event) => {
    const rendered = event?.promise instanceof Promise ? event.promise.catch(() => {}) : Promise.resolve();

    rendered.then(() => {
      this.#updateCardSliders();
      reapplyWishlistIcons();
    });
  };

  /**
   * In the densest grid views (desktop x8 / mobile x4) the cards are too small for a
   * carousel, so each card's slideshow is disabled via the same `disabled` attribute the
   * theme uses (which stops native scroll, JS drag and arrows). Slideshows already
   * disabled server-side (2-image products, carousel off) are preserved via a marker.
   */
  #updateCardSliders = () => {
    const { grid } = this.refs;
    if (!grid) return;

    const view = grid.getAttribute('product-grid-view');
    const dense = view === 'zoom-out' || view === 'mobile-zoom-out';

    for (const slider of grid.querySelectorAll('.card-gallery slideshow-component')) {
      if (dense) {
        if (slider.getAttribute('disabled') !== 'true') {
          resetToFeaturedSlide(slider);
          slider.setAttribute('disabled', 'true');
          slider.setAttribute('data-view-disabled', '');
        }
      } else if (slider.hasAttribute('data-view-disabled')) {
        slider.removeAttribute('disabled');
        slider.removeAttribute('data-view-disabled');
      }
    }
  };

  /**
   * Re-applies the per-card slider state on view change or when cards are appended.
   * @type {MutationObserver}
   */
  #cardSliderObserver = new MutationObserver(() => this.#updateCardSliders());

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';

    // The density the merchant configured for the viewport being entered - on the
    // collection or in theme settings - which is what the first crossing lands on
    // when nothing is stored for it yet. Already narrowed to a known view
    // server-side, so it needs no check below.
    const { grid } = this.refs;
    const merchantDefault =
      (viewport === 'desktop' ? grid?.dataset.defaultViewDesktop : grid?.dataset.defaultViewMobile) || 'default';

    const storedView = sessionStorage.getItem(`product-grid-view-${viewport}`);

    // The density controls may live in another section (grid-density block), so
    // the radios are looked up document-wide. Same rule as the restore script in
    // product-grid.liquid, which has to state it again because it runs before the
    // modules load and cannot import: only a stored value is suspect, and with no
    // radios to check it against the merchant's default stands in. Coercing to
    // 'default' here would undo a density that script had just applied.
    const groupName = viewport === 'desktop' ? 'grid' : 'grid-mobile';
    const options = /** @type {NodeListOf<HTMLInputElement>} */ (
      document.querySelectorAll(`input[type="radio"][name="${groupName}"]`)
    );
    const isUsable =
      options.length === 0 ? storedView != null : Array.from(options).some((option) => option.value === storedView);
    const value = isUsable ? storedView : merchantDefault;

    for (const option of options) {
      option.checked = option.value === value;
    }

    this.#setLayout(value);
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
